import React, { KeyboardEvent, ReactNode } from "react";
import Flex from "common/Boxes/Flex";
import styled from "styled-components";
import SubmitButton from "features/onboarding/common/SubmitButton";
import Text from "common/Typography/Text";
import {
  CODE_BOX_HEIGHT,
  onboardingStyles,
} from "@shared/features/onboarding/styles";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import useVerificationCode from "@shared/features/onboarding/useVerificationCode";
import { openMessage } from "utils/misc";

interface VerificationCodeProps {
  numOfDigits: number;
  title?: string;
  onSubmit: (code: string) => void;
  isLoading: boolean;
  buttonText?: string;
  defaultCode?: string;
  actionButton?: ReactNode;
  additionalItems?: ReactNode;
}

const VerificationCode = ({
  numOfDigits,
  title,
  onSubmit,
  isLoading,
  buttonText,
  defaultCode,
  actionButton,
  additionalItems,
}: VerificationCodeProps) => {
  const { onBackPress, hiddenInputProps, buttonProps, boxesData } =
    useVerificationCode({
      numOfDigits,
      onSubmit,
      openMessage,
      defaultCode,
    });

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      onBackPress();
    }
  };

  const verificationCode = (
    <div style={{ position: "relative" }}>
      <Flex $justifyContent="center">
        {boxesData.map((boxData, index) => {
          return (
            <InputBox
              key={index}
              style={boxData.style}
              $isSelected={boxData.isSelected}
              onClick={boxData.onClick}
            >
              <Text $size={"L"}>{boxData.text}</Text>
            </InputBox>
          );
        })}
      </Flex>
      <HiddenInput
        value={hiddenInputProps.value}
        ref={hiddenInputProps.ref}
        onChange={hiddenInputProps.onChange}
        onKeyDown={onKeyPress}
        onBlur={hiddenInputProps.onBlur}
        onFocus={hiddenInputProps.onFocus}
        stringMode={true}
      />
    </div>
  );

  return (
    <CodeWrapper>
      <InputTitle>{title}</InputTitle>
      {verificationCode}
      {actionButton && (
        <Flex style={{ marginTop: 8 }} $justifyContent="flex-end">
          {actionButton}
        </Flex>
      )}
      {additionalItems}
      <StyledSubmitButton
        onClick={buttonProps.onClick}
        disabled={buttonProps.disabled}
        loading={isLoading}
      >
        {buttonText || "Verify"}
      </StyledSubmitButton>
    </CodeWrapper>
  );
};

export default VerificationCode;

const CodeWrapper = styled.div`
  width: 100%;
  max-width: 340px;
`;
const InputBox = styled.div<{ $isSelected: boolean }>`
  ${onboardingStyles.verificationCodeBox};
  border-style: solid;
`;
const InputTitle = styled(Text)`
  margin: 32px 0 8px 0;
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
`;
const StyledSubmitButton = styled(SubmitButton)`
  width: 100%;
  margin: 24px 0 12px 0;
`;
const HiddenInput = styled(CustomInputNumber)`
  position: absolute;
  top: 0;
  left: 0;
  height: ${CODE_BOX_HEIGHT}px;
  opacity: 0;
`;
